import LogoSvg from '@icons/logo.svg?react';
import classNames from 'classnames';
import { type FC, useEffect, useState } from 'react';

export interface AvatarProps {
  /** Unique identifier for color generation */
  uuid?: string;
  /** Source URL for the avatar image */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Name to generate initials from (first letter of first and last name) */
  name?: string;
  /** Custom initials to display (overrides name-generated initials) */
  initials?: string;
  /** Size of the avatar */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Shape of the avatar */
  shape?: 'circle' | 'square' | 'rounded';
  /** Additional CSS classes */
  className?: string;
  /** Background color for initials avatar */
  backgroundColor?: string;
  /** Text color for initials */
  textColor?: string;
  /** Click handler */
  onClick?: () => void;
  mode?: 'light' | 'dark';
}

const Avatar: FC<AvatarProps> = ({
  src,
  alt,
  name,
  initials,
  size = 'md',
  shape = 'circle',
  className,
  backgroundColor,
  textColor,
  onClick,
  uuid,
  mode = 'light',
}) => {
  // Hash uuid to HSL color
  const getColorFromUuid = (uuid: string, scheme: 'light' | 'dark', forBackground = true) => {
    let hash = 0;
    for (let i = 0; i < uuid.length; i++) {
      hash = uuid.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Use hash to get hue, and adjust lightness for colorScheme
    const hue = Math.abs(hash) % 360;
    const darkLightness = forBackground ? 12 : 52;
    const lightLightness = forBackground ? 92 : 32;
    const lightness = scheme === 'dark' ? darkLightness : lightLightness;
    const saturation = 70;
    return `hsl(${hue},${saturation}%,${lightness}%)`;
  };

  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState(src);
  const [bgColor, setBgColor] = useState<string>();
  const [txtColor, setTxtColor] = useState<string>();

  useEffect(() => {
    if (src) {
      setImageUrl(src);
    }
  }, [src]);

  useEffect(() => {
    if (uuid && !backgroundColor) {
      setBgColor(getColorFromUuid(uuid, mode, true));
      setTxtColor(getColorFromUuid(uuid, mode, false));
    }
  }, [uuid, backgroundColor, mode]);

  // Helper function to detect if a string contains Chinese characters
  const containsChinese = (text: string): boolean => {
    return /[\u4e00-\u9fff]/.test(text);
  };

  // Generate initials from name
  const getInitialsFromName = (name: string): string => {
    const words = name.trim().split(/\s+/);

    if (words.length === 1) {
      const singleName = words[0];

      if (containsChinese(singleName)) {
        // Chinese name without spaces (e.g., "张小明", "李伟")
        if (singleName.length >= 3) {
          // 3+ characters: last name (1 char) + first name (2+ chars)
          // Use the 2nd and 3rd characters (first name)
          return singleName.substring(1, 3);
        } else if (singleName.length === 2) {
          // 2 characters: could be last+first (1+1), use 1 from each
          return singleName.charAt(0) + singleName.charAt(1);
        } else {
          // Single character
          return singleName;
        }
      } else {
        // Non-Chinese single word - use first two characters
        return singleName.substring(0, 2).toUpperCase();
      }
    } else {
      // Multiple words - assume Western naming convention
      return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    }
  };

  // Determine what to display
  const displayInitials = initials || (name ? getInitialsFromName(name) : undefined);
  const showImage = name === undefined && imageUrl && !imageError;

  // Size classes
  const sizeClasses = {
    xs: 'w-6 h-6 text-[0.55rem]',
    sm: 'w-8 h-8 text-[0.65rem]',
    md: 'w-10 h-10 text-xs',
    lg: 'w-12 h-12 text-sm',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-20 h-20 text-3xl',
  };

  // Shape classes
  const shapeClasses = {
    circle: 'rounded-full',
    square: 'rounded-none',
    rounded: 'rounded-box',
  };

  // Default colors for initials
  let generatedBg: string | undefined;
  const defaultBackgroundColor = backgroundColor
    ? backgroundColor
    : generatedBg
      ? undefined
      : 'bg-primary/20 shadow shadow-base-300';
  const defaultTextColor = textColor || 'text-base-content';

  const avatarClasses = classNames(
    'flex items-center justify-center font-semibold select-none overflow-hidden',
    sizeClasses[size],
    shapeClasses[shape],
    {
      ...(defaultBackgroundColor && !generatedBg && !showImage
        ? { [defaultBackgroundColor]: true }
        : {}),
      [defaultTextColor]: !showImage,
      'cursor-pointer': onClick,
      'hover:opacity-80 transition-opacity': onClick,
    },
    className,
  );

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div
      className={avatarClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      style={bgColor ? { background: bgColor } : undefined}
    >
      {showImage && (
        <img
          src={imageUrl}
          alt={alt || name || 'Avatar'}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      )}

      {displayInitials && (
        <span className="font-medium" style={{ color: txtColor }}>
          {displayInitials}
        </span>
      )}

      {!showImage && !displayInitials && uuid && (
        <LogoSvg
          className="w-2/3 h-2/3 object-contain"
          style={{
            fill: txtColor,
          }}
        />
      )}
    </div>
  );
};

export default Avatar;
